import {
  amplitudeIdentify,
  amplitudeTrack,
  useAmplitudeKey,
} from "@/utils/hooks/use-amplitude-pageview";
import { useNextEnvVars } from "@/components/context-providers/NextEnvVarsWrapper";
import { useStableObject } from "@/utils/hooks/use-stable-object";
import { useRouter } from "next/compat/router";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { ValidPropertyType } from "@amplitude/analytics-types";

export const AmplitudeScript = () => {
  return <></>;
};

export enum AmplitudeEvent {
  VIEW_MARKETING_PAGE = "view marketing page",
  MARKETING_CLICK = "marketing page click",
  MARKETING_SUBMIT_HUBSPOT = "marketing: submit hubspot form",
  VIEW_PAGE = "view page",
  BRANCH_NAVBAR_CLICK = "navigation: branch sidebar click",
  DEPLOYMENT_NAVBAR_CLICK = "navigation: deployment sidebar click",
  SETTINGS_NAVBAR_CLICK = "navigation: settings sidebar click",
  PROJECT_NAVBAR_CLICK = "navigation: main project sidebar click",
  TEAM_NAVBAR_CLICK = "navigation: main team sidebar click",
  TEST_NAVBAR_CLICK = "navigation: test components sidebar click",
  TOGGLE_DARK_MODE = "navigation: toggle dark mode",
  NAVBAR_CHANGE_LAYOUT = "navigation: toggle v2 layout",
  TAB_ITEM_CLICK = "navigation: tab item click",
  DESIGN_SYSTEM_BUTTON_CLICK = "button click",
  DESIGN_SYSTEM_TAB_CLICK = "tab click",
  DESIGN_SYSTEM_CHECKBOX_CLICK = "checkbox click",
  DESIGN_SYSTEM_SELECTION_CHANGE = "design system: selection change",
  NAVIGATION_SUBMIT_FEEDBACK = "navigation: submit feedback",

  // Product usage events use "<area>: <action>" names; the event type is the
  // stable key in Amplitude charts, so renaming one splits its history.

  // --- notebooks ---
  NOTEBOOK_VIEW = "notebook: view",
  NOTEBOOK_CREATE = "notebook: create",
  NOTEBOOK_CELL_CREATE = "notebook: cell create",
  NOTEBOOK_CELL_CONVERT = "notebook: cell convert",
  NOTEBOOK_CELL_EXECUTE = "notebook: cell execute",
  NOTEBOOK_APP_VIEW = "notebook: app view",
  NOTEBOOK_APP_TOGGLE = "notebook: app toggle",
  NOTEBOOK_APP_LAYOUT_EDIT = "notebook: app layout edit",
  NOTEBOOK_SHARE_LINK_CREATE = "notebook: share link create",
  NOTEBOOK_SHARE_LINK_REVOKE = "notebook: share link revoke",
  // --- datasources ---
  DATASOURCE_CREATE_START = "datasource: create start",
  DATASOURCE_CREATE = "datasource: create",
  DATASOURCE_TEST_CONNECTION = "datasource: test connection",
  DATASOURCE_UPDATE = "datasource: update",
  DATASOURCE_DELETE = "datasource: delete",
  // --- dashboards ---
  DASHBOARD_LIST_VIEW = "dashboard: list view",
  DASHBOARD_CREATE = "dashboard: create",
  DASHBOARD_VIEW = "dashboard: view",
  DASHBOARD_DELETE = "dashboard: delete",
  DASHBOARD_EXPORT = "dashboard: export",
  DASHBOARD_WIDGET_ADD = "dashboard: widget add",
  // --- ai assistant ---
  AI_ASSISTANT_OPEN = "ai assistant: open",
  AI_ASSISTANT_SESSION_CREATE = "ai assistant: session create",
  AI_ASSISTANT_SESSION_VIEW = "ai assistant: session view",
  AI_ASSISTANT_MESSAGE_SEND = "ai assistant: message send",
  AI_ASSISTANT_TOOL_CALL_DECIDE = "ai assistant: tool call decide",
  AI_ASSISTANT_STOP = "ai assistant: stop",
  AI_ASSISTANT_RETRY = "ai assistant: retry",
  AI_ASSISTANT_FORK = "ai assistant: fork",
  AI_ASSISTANT_FEEDBACK = "ai assistant: feedback",
  // --- nudge cards ---
  NUDGE_CARD_DISMISS = "nudge card: dismiss",
  NUDGE_CARD_CLICK_ACTION = "nudge card: click action",
  NUDGE_CARD_HOVER_MOUSE = "nudge card: hover mouse",
  // --- ai connections ---
  AI_CONNECTION_CREATE = "ai connection: create",
  AI_CONNECTION_UPDATE = "ai connection: update",
  AI_CONNECTION_DELETE = "ai connection: delete",
  AI_CONNECTION_SET_DEFAULT = "ai connection: set default",
  AI_CONNECTION_UNSET_DEFAULT = "ai connection: unset default",
  // --- slack ---
  SLACK_INSTALL_START = "slack: install start",
  SLACK_INSTALL = "slack: install",
  // --- github ---
  GITHUB_LINK_START = "github: link start",
  GITHUB_LINK = "github: link",
  GITHUB_CONNECT_REPOSITORY = "github: connect repository",
  // --- environments ---
  ENVIRONMENT_CREATE_START = "environment: create start",
  ENVIRONMENT_CREATE = "environment: create",
  // --- signup ---
  SIGNUP_ENVIRONMENT_CREATE = "signup: environment create",
  // --- team settings ---
  TEAM_SETTINGS_VIEW = "team settings: view",
  TEAM_SETTINGS_DETAIL_VIEW = "team settings: detail view",
  // --- permissions ---
  PERMISSIONS_ROLE_CHANGE = "permissions: role change",
  PERMISSIONS_USER_DEACTIVATE = "permissions: user deactivate",
  PERMISSIONS_USER_REACTIVATE = "permissions: user reactivate",
  PERMISSIONS_CUSTOM_ROLE_CREATE = "permissions: custom role create",
  PERMISSIONS_CUSTOM_ROLE_UPDATE = "permissions: custom role update",
  PERMISSIONS_CUSTOM_ROLE_DELETE = "permissions: custom role delete",
  PERMISSIONS_ROLE_FEATURE_TAGS_UPDATE = "permissions: role feature tags update",
  PERMISSIONS_SCIM_TOKEN_CREATE = "permissions: scim token create",
  PERMISSIONS_SCIM_GROUP_SETTINGS_UPDATE = "permissions: scim group settings update",
  PERMISSIONS_ACCESS_TOKEN_CREATE = "permissions: access token create",
  PERMISSIONS_ACCESS_TOKEN_UPDATE = "permissions: access token update",
  PERMISSIONS_ACCESS_TOKEN_REVOKE = "permissions: access token revoke",
  // --- invites ---
  INVITE_START = "invite: start",
  INVITE_SEND = "invite: send",
  INVITE_RESEND = "invite: resend",
  INVITE_REVOKE = "invite: revoke",
  INVITE_VIEW_LINK = "invite: view link",
  INVITE_ACCEPT = "invite: accept",
  // --- deployments ---
  DEPLOYMENT_REDEPLOY_START = "deployment: redeploy start",
  DEPLOYMENT_REDEPLOY = "deployment: redeploy",
  // --- compute ---
  COMPUTE_LIST_VIEW = "compute: list view",
  COMPUTE_DETAIL_VIEW = "compute: detail view",
  COMPUTE_CREATE = "compute: create",
  COMPUTE_UPDATE = "compute: update",
  COMPUTE_DELETE = "compute: delete",
  COMPUTE_ACTION = "compute: action",
  // --- plan viewer ---
  PLAN_VIEWER_RESET_ZOOM = "plan viewer: reset zoom",
  PLAN_VIEWER_RECENTER_NODE = "plan viewer: recenter node",
  PLAN_VIEWER_CHANGE_LAYOUT_FLOW = "plan viewer: change layout flow",
  PLAN_VIEWER_CHANGE_OPERATOR_VIEW = "plan viewer: change operator view",
  PLAN_VIEWER_TOGGLE_REUSE_NODES = "plan viewer: toggle reuse nodes",
  PLAN_VIEWER_SEARCH_NODE = "plan viewer: search node",
  PLAN_VIEWER_HIDE_MINIMAP = "plan viewer: hide minimap",
  PLAN_VIEWER_SHOW_MINIMAP = "plan viewer: show minimap",
  PLAN_VIEWER_MINIMAP_ZOOM_IN = "plan viewer: minimap zoom in",
  PLAN_VIEWER_MINIMAP_ZOOM_OUT = "plan viewer: minimap zoom out",
  PLAN_VIEWER_NODE_CLICK = "plan viewer: node click",
  PLAN_VIEWER_DETAIL_PANE_TAB_CLICK = "plan viewer: detail pane tab click",
  // --- time selector ---
  TIME_SELECTOR_ACTION = "time selector: action",
}

export type AmplitudeEventProperties = Record<string, unknown>;

const AmplitudeEventPropertiesContext = createContext<AmplitudeEventProperties>({});

export const AmplitudeEventPropertiesProvider: React.FC<
  PropsWithChildren<{ properties: AmplitudeEventProperties }>
> = ({ properties, children }) => {
  const parentProperties = useContext(AmplitudeEventPropertiesContext);
  // Callers pass inline objects; keeping the context value stable across equal renders keeps
  // every useAmplitudeTrack callback stable too.
  const stableProperties = useStableObject(properties);
  const mergedProperties = useMemo(
    () => ({ ...parentProperties, ...stableProperties }),
    [parentProperties, stableProperties],
  );

  return (
    <AmplitudeEventPropertiesContext.Provider value={mergedProperties}>
      {children}
    </AmplitudeEventPropertiesContext.Provider>
  );
};

export const useAmplitudeEventProperties = () => useContext(AmplitudeEventPropertiesContext);

export interface AmplitudeOnClickOpts {
  event?: AmplitudeEvent;
  component?: string;
  href?: string;
  text?: string;
  description?: string;
  extraProperties?: Record<string, unknown>;
}

export function useAmplitudeOnClick(opts: AmplitudeOnClickOpts) {
  const router = useRouter();
  const eventProperties = useAmplitudeEventProperties();
  const stableProperties = useStableObject(opts.extraProperties ?? {});
  const currentPath =
    router?.asPath ??
    (typeof window === "undefined"
      ? undefined
      : `${window.location.pathname}${window.location.search}${window.location.hash}`);

  const NEXT_PUBLIC_AMPLITUDE_API_KEY = useAmplitudeKey();

  return useCallback(
    (additionalProperties?: Record<string, unknown>) => {
      if (opts.event == null) {
        return;
      }

      amplitudeTrack(NEXT_PUBLIC_AMPLITUDE_API_KEY, opts.event, {
        ...eventProperties,
        path: currentPath,
        location: typeof window === "undefined" ? undefined : window.location.href,
        component: opts.component,
        href: opts.href,
        text: opts.text,
        description: opts.description,
        ...stableProperties,
        ...additionalProperties,
      });
    },
    [
      opts.event,
      opts.component,
      opts.href,
      opts.text,
      opts.description,
      NEXT_PUBLIC_AMPLITUDE_API_KEY,
      currentPath,
      stableProperties,
      eventProperties,
    ],
  );
}

/**
 * Returns a stable callback that tracks a product usage event, merged over the
 * enclosing AmplitudeEventPropertiesProvider properties and the current path.
 * The callback reads the path at call time, so it is safe to call from async
 * handlers (e.g. after a mutation resolves) and from non-click triggers.
 * Tracking is best-effort: it is a no-op without an Amplitude key and never
 * throws into the caller.
 */
export function useAmplitudeTrack() {
  const eventProperties = useAmplitudeEventProperties();
  const { NEXT_PUBLIC_AMPLITUDE_API_KEY } = useNextEnvVars();

  return useCallback(
    (event: AmplitudeEvent, properties?: AmplitudeEventProperties) => {
      try {
        amplitudeTrack(NEXT_PUBLIC_AMPLITUDE_API_KEY, event, {
          ...eventProperties,
          path: typeof window === "undefined" ? undefined : window.location.pathname,
          ...properties,
        });
      } catch (err: unknown) {
        console.error(err);
      }
    },
    [NEXT_PUBLIC_AMPLITUDE_API_KEY, eventProperties],
  );
}

/**
 * Tracks `event` once per mount for each distinct `key` (e.g. a dashboard id),
 * for "viewed X" events. Pass `key = null` to defer until the data it depends
 * on (such as a name) has loaded.
 */
export function useAmplitudeTrackView(
  event: AmplitudeEvent,
  key: string | null,
  properties: AmplitudeEventProperties,
) {
  const track = useAmplitudeTrack();
  const stableProperties = useStableObject(properties);
  const lastTrackedKeyRef = useRef<string | null>(null);

  useEffect(() => {
    if (key == null || lastTrackedKeyRef.current === key) {
      return;
    }
    lastTrackedKeyRef.current = key;
    track(event, stableProperties);
  }, [event, key, stableProperties, track]);
}

export function useAmplitudeIdentify() {
  const NEXT_PUBLIC_AMPLITUDE_API_KEY = useAmplitudeKey();

  return useCallback(
    (properties?: Record<string, ValidPropertyType | null | undefined>) => {
      amplitudeIdentify(NEXT_PUBLIC_AMPLITUDE_API_KEY, {
        ...properties,
      });
    },
    [NEXT_PUBLIC_AMPLITUDE_API_KEY],
  );
}
